import { useCallback, useMemo, useRef, useState } from 'react';

import {
  getCalendarPixelsPerMinute,
  type OverlapLayoutParams,
} from '@pages/Calendar/utils';

import { BOOKING_STEP_MINUTES } from '@pages/Calendar/data';
import type { CalendarEvent } from '@pages/Calendar/data';
import {
  calculateDraggedRange,
  calculateResizedBottomRange,
  calculateResizedTopRange,
  getTargetEmployeeIdFromPoint,
  toMinutes,
} from '@pages/Calendar/utils/appointmentDragUtils';
import type { DragRange } from '@pages/Calendar/utils/appointmentDragUtils';
import { getRecordDisplayIdentity } from '@pages/Calendar/utils/getRecordDisplayIdentity';

import type { PointerEvent as ReactPointerEvent } from 'react';

type InteractionMode = 'idle' | 'move' | 'resize-top' | 'resize-bottom';

export interface PendingMove {
  newStart: string;
  newEnd: string;
  /** Employee id where the card was dropped — equals event.employeeId when no column change */
  newEmployeeId: string;
}

export interface EventDragState {
  eventId: string;
  sourceEmployeeId: string;
  targetEmployeeId: string;
  previewTopPx: number;
  previewHeightPx: number;
  previewStart: string;
  previewEnd: string;
  previewTitle: string;
  clientName?: string;
  phone?: string;
  services?: string[];
}

interface UseAppointmentInteractionProps {
  event: CalendarEvent;
  layout: OverlapLayoutParams;
  calendarStartTime: string;
  calendarEndTime: string;
  stepMinutes?: number;
  intervalHeightPx: number;
  onEventUpdate: (
    eventId: string,
    newStart: string,
    newEnd: string,
    newEmployeeId?: string
  ) => void;
  onDragStateChange?: (state: EventDragState | null) => void;
  onRecordClick?: (event: CalendarEvent) => void;
  canShowContacts: boolean;
}

export const useAppointmentInteraction = ({
  event,
  layout,
  calendarStartTime,
  calendarEndTime,
  stepMinutes = BOOKING_STEP_MINUTES,
  intervalHeightPx,
  onEventUpdate,
  onDragStateChange,
  onRecordClick,
  canShowContacts,
}: UseAppointmentInteractionProps) => {
  const modeRef = useRef<InteractionMode>('idle');
  const initialPointerY = useRef(0);
  const previewRef = useRef<DragRange | null>(null);
  const targetEmployeeIdRef = useRef<string>(event.employeeId);
  const hasMovedRef = useRef(false);

  const [interactionMode, setInteractionMode] =
    useState<InteractionMode>('idle');
  const [preview, setPreviewState] = useState<DragRange | null>(null);
  const [pendingMove, setPendingMove] = useState<PendingMove | null>(null);
  const [previewEmployeeId, setPreviewEmployeeId] = useState<string>(
    event.employeeId
  );

  const calendarStartMinutes = toMinutes(calendarStartTime);
  const calendarEndMinutes = toMinutes(calendarEndTime);
  const originalStartMinutes = toMinutes(event.start);
  const originalEndMinutes = toMinutes(event.end);
  const durationMinutes = originalEndMinutes - originalStartMinutes;
  const pixelsPerMinute = getCalendarPixelsPerMinute(intervalHeightPx);

  const displayIdentity = useMemo(
    () =>
      getRecordDisplayIdentity({
        record: event.rawRecord,
        canShowContacts,
        fallbackClientName: event.clientName,
        fallbackPhone: event.phone,
      }),
    [canShowContacts, event.clientName, event.phone, event.rawRecord]
  );

  const setPreview = useCallback((value: DragRange | null) => {
    previewRef.current = value;
    setPreviewState(value);
  }, []);

  const startInteraction = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>, mode: InteractionMode) => {
      e.stopPropagation();
      e.currentTarget.setPointerCapture(e.pointerId);
      modeRef.current = mode;
      setInteractionMode(mode);
      initialPointerY.current = e.clientY;
      hasMovedRef.current = false;
      // Reset target employee on every new interaction start
      targetEmployeeIdRef.current = event.employeeId;
      setPreviewEmployeeId(event.employeeId);
    },
    [event.employeeId]
  );

  const handleBodyPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      startInteraction(e, 'move');
    },
    [startInteraction]
  );

  const handleResizeTopPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      e.stopPropagation();
      startInteraction(e, 'resize-top');
    },
    [startInteraction]
  );

  const handleResizeBottomPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      e.stopPropagation();
      startInteraction(e, 'resize-bottom');
    },
    [startInteraction]
  );

  const handlePointerMove = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      const mode = modeRef.current;

      if (mode === 'idle') return;

      e.preventDefault();

      const deltaPixels = e.clientY - initialPointerY.current;

      if (Math.abs(deltaPixels) > 4) {
        hasMovedRef.current = true;
      }

      let range: DragRange;

      if (mode === 'move') {
        range = calculateDraggedRange({
          deltaPixels,
          originalStartMinutes,
          durationMinutes,
          calendarStartMinutes,
          calendarEndMinutes,
          stepMinutes,
          pixelsPerMinute,
        });

        // Determine which employee column is currently under the pointer
        const hoveredEmployeeId =
          getTargetEmployeeIdFromPoint(e.clientX, e.clientY) ??
          event.employeeId;

        if (hoveredEmployeeId !== targetEmployeeIdRef.current) {
          targetEmployeeIdRef.current = hoveredEmployeeId;
          setPreviewEmployeeId(hoveredEmployeeId);
        }

        onDragStateChange?.({
          eventId: event.id,
          sourceEmployeeId: event.employeeId,
          targetEmployeeId: targetEmployeeIdRef.current,
          previewTopPx: range.newTopPx,
          previewHeightPx: range.newHeightPx,
          previewStart: range.newStart,
          previewEnd: range.newEnd,
          previewTitle: event.title,
          clientName: displayIdentity.clientName || undefined,
          phone: displayIdentity.phone,
          services: event.services,
        });
      } else if (mode === 'resize-bottom') {
        range = calculateResizedBottomRange({
          deltaPixels,
          originalStartMinutes,
          originalEndMinutes,
          calendarStartMinutes,
          calendarEndMinutes,
          stepMinutes,
          pixelsPerMinute,
        });
      } else {
        range = calculateResizedTopRange({
          deltaPixels,
          originalStartMinutes,
          originalEndMinutes,
          calendarStartMinutes,
          stepMinutes,
          pixelsPerMinute,
        });
      }

      setPreview(range);
    },
    [
      originalStartMinutes,
      originalEndMinutes,
      durationMinutes,
      calendarStartMinutes,
      calendarEndMinutes,
      stepMinutes,
      pixelsPerMinute,
      event.employeeId,
      event.id,
      event.title,
      displayIdentity.clientName,
      displayIdentity.phone,
      onDragStateChange,
      setPreview,
      event.services,
    ]
  );

  const handlePointerUp = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      e.currentTarget.releasePointerCapture(e.pointerId);

      const currentMode = modeRef.current;

      modeRef.current = 'idle';
      setInteractionMode('idle');

      const currentPreview = previewRef.current;

      previewRef.current = null;
      setPreviewState(null);

      const finalEmployeeId = targetEmployeeIdRef.current;

      // Reset preview employee back to original immediately
      setPreviewEmployeeId(event.employeeId);
      targetEmployeeIdRef.current = event.employeeId;
      onDragStateChange?.(null);

      // No significant movement → treat as a click on the record
      if (currentMode === 'move' && !hasMovedRef.current) {
        onRecordClick?.(event);

        return;
      }

      if (!currentPreview) return;

      const { newStart, newEnd } = currentPreview;
      const timeChanged = newStart !== event.start || newEnd !== event.end;
      const employeeChanged = finalEmployeeId !== event.employeeId;
      const changed = timeChanged || employeeChanged;

      if (!changed) return;

      if (currentMode === 'move') {
        setPendingMove({ newStart, newEnd, newEmployeeId: finalEmployeeId });
      } else {
        // resize — apply immediately without confirmation modal
        onEventUpdate(event.id, newStart, newEnd);
      }
    },
    [event, onEventUpdate, onDragStateChange, onRecordClick]
  );

  const clearPendingMove = useCallback(() => {
    setPendingMove(null);
  }, []);

  const previewTopPx = preview?.newTopPx ?? layout.topPx;
  const previewHeightPx = preview?.newHeightPx ?? layout.heightPx;
  const previewStart = preview?.newStart ?? event.start;
  const previewEnd = preview?.newEnd ?? event.end;

  return {
    interactionMode,
    preview,
    pendingMove,
    previewTopPx,
    previewHeightPx,
    previewStart,
    previewEnd,
    previewEmployeeId,
    handleBodyPointerDown,
    handleResizeTopPointerDown,
    handleResizeBottomPointerDown,
    handlePointerMove,
    handlePointerUp,
    clearPendingMove,
  };
};
